/**
 * Utility functions for system announcements.
 */
import type { AnnouncementSeverity } from '../types';

/** Priority order for severity levels (higher = more important) */
export const SEVERITY_PRIORITY: Record<AnnouncementSeverity, number> = {
  info: 1,
  warning: 2,
  critical: 3,
};

/**
 * Select the highest priority announcement from a list.
 * Filters out dismissed announcements and picks the highest severity.
 *
 * @param announcements - List of announcements
 * @param dismissedIds - Set of dismissed announcement IDs
 * @returns The highest priority announcement, or null if none available
 */
export function selectHighestPriorityAnnouncement<
  T extends { id: string; severity: AnnouncementSeverity },
>(announcements: T[] | undefined, dismissedIds: Set<string>): T | null {
  if (!announcements || announcements.length === 0) return null;

  // Filter out dismissed announcements
  const available = announcements.filter((a) => !dismissedIds.has(a.id));
  if (available.length === 0) return null;

  // Single-pass: track best priority, keep first on tie (stable)
  return available.reduce<T | null>(
    (best, curr) =>
      !best || SEVERITY_PRIORITY[curr.severity] > SEVERITY_PRIORITY[best.severity] ? curr : best,
    null
  );
}
